/**
 * What an image variation depends on — the campaign keys whose values it is
 * generated for, and its reference images — and whether they have changed
 * since.
 *
 * A variation depends on its template entry's `campaignKey`, extended by the
 * step's `campaignStructure`: the `campaignKey` of every cell and field linked
 * to it (`variationKey`) and, in a `fields` grid, that field's `value`. When an
 * image is generated, the values of those keys are recorded on the generated
 * asset as its `sourceValue` (`{ [campaignKey]: value }`). A value there that
 * differs from the campaign's now makes the image outdated — until it is
 * generated again.
 *
 * Its reference images count too: the file of each one an image is generated
 * from is recorded on the generated asset (`sourceReferences`), and a file
 * that has changed since makes the image outdated as well.
 *
 * An image the user uploaded records neither, so it is never outdated.
 *
 * Pure (no React), so it can be checked on its own.
 */

import { GRID_TYPE, getGroupGrids, isTextCell } from "./wizardSteps";

/** A campaign key or a list of them, as a list of distinct keys. */
export const toKeyList = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [value]).filter(
        (key) => typeof key === "string" && !!key,
      ),
    ),
  );

/**
 * What a step's `campaignStructure` adds to the dependencies of the variations
 * it shows, by variation key: the `keys` of every place a variation appears,
 * and the `labels` of the fields that show them.
 *
 * @returns {Map<string, { keys: string[], labels: Map<string, string> }>}
 */
export const getStructureDependencies = (campaignStructure) => {
  const byKey = new Map();
  // Every campaign key the structure shows, labelled by the first field
  // showing it — shared by all of its variations.
  const labels = new Map();

  for (const group of campaignStructure ?? []) {
    for (const { type, rows } of getGroupGrids(group)) {
      for (const cell of rows.flat().filter(Boolean)) {
        const isFieldsGrid = type !== GRID_TYPE.VARIATIONS;
        const isText = !isFieldsGrid && isTextCell(cell);

        // A field (or text cell) names the keys it shows.
        if ((isFieldsGrid || isText) && cell.label) {
          toKeyList(cell.value).forEach((key) => {
            if (!labels.has(key)) {
              labels.set(key, cell.label);
            }
          });
        }

        // A text cell shows no image, so it links no variation.
        if (isText || !cell.variationKey) {
          continue;
        }

        const keys = [
          ...toKeyList(cell.campaignKey),
          ...(isFieldsGrid ? toKeyList(cell.value) : []),
        ];

        const entry = byKey.get(cell.variationKey) ?? { keys: [], labels };

        entry.keys = toKeyList([...entry.keys, ...keys]);
        byKey.set(cell.variationKey, entry);
      }
    }
  }

  return byKey;
};

/**
 * Every campaign key a variation depends on: its template entry's
 * `campaignKey`, then the ones `structureDependencies` adds (see
 * `getStructureDependencies`).
 */
export const getVariationDependencies = (
  templateVariation,
  structureDependencies,
) =>
  toKeyList([
    ...toKeyList(templateVariation?.campaignKey),
    ...(structureDependencies?.keys ?? []),
  ]);

/**
 * The values of `keys` in `campaign`, as recorded on a variation when its
 * prompt is written — `undefined` when it depends on nothing.
 */
export const snapshotDependencies = (keys, campaign) =>
  keys?.length
    ? Object.fromEntries(
        keys.map((key) => [key, String(campaign?.[key] ?? "")]),
      )
    : undefined;

const isSnapshot = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * The `keys` whose value in `campaign` differs from the one recorded in
 * `sourceValue`. A key it did not record — added since, or unknown where the
 * prompt was written — is not compared, nor is anything but a snapshot.
 */
export const getChangedDependencies = (sourceValue, keys, campaign) =>
  isSnapshot(sourceValue)
    ? (keys ?? []).filter(
        (key) =>
          Object.hasOwn(sourceValue, key) &&
          String(sourceValue[key] ?? "") !== String(campaign?.[key] ?? ""),
      )
    : [];

/**
 * The file of every reference image an image is generated from, as recorded
 * on the generated asset (`sourceReferences`) — `null` for a reference that
 * does not exist yet. `undefined` when it has no references.
 */
export const snapshotReferences = (referenceKeys, assets) =>
  referenceKeys?.length
    ? Object.fromEntries(
        referenceKeys.map((key) => [
          key,
          assets?.find((asset) => asset.name === key)?.filePath ?? null,
        ]),
      )
    : undefined;

/**
 * The `referenceKeys` whose file in `assets` differs from the one recorded in
 * `sourceReferences` — a reference missing then (`null`) and added since
 * included. A reference it did not record is not compared, nor is anything
 * but a snapshot.
 */
export const getChangedReferences = (
  sourceReferences,
  referenceKeys,
  assets,
) =>
  isSnapshot(sourceReferences)
    ? (referenceKeys ?? []).filter(
        (key) =>
          Object.hasOwn(sourceReferences, key) &&
          (sourceReferences[key] ?? null) !==
            (assets?.find((asset) => asset.name === key)?.filePath ?? null),
      )
    : [];

/**
 * The keys of the `variations` that an edit of the campaign from
 * `previousCampaign` to `nextCampaign` changed the source of: a campaign key
 * they depend on (see `getVariationDependencies`) changed value, or their
 * prompt template — resolved by `getPromptTemplate(variation, campaign)` —
 * reads differently. The latter catches the campaign fields a variation reads
 * through its `values` or prompt without declaring them as `campaignKey`.
 *
 * The campaigns' identity (`id`, `thread_id`) is not part of the comparison.
 */
export const getCampaignChangedVariationKeys = (
  variations,
  previousCampaign,
  nextCampaign,
  { structureDependencies, getPromptTemplate } = {},
) => {
  if (!previousCampaign || !nextCampaign) {
    return [];
  }

  const next = {
    ...nextCampaign,
    id: previousCampaign.id,
    thread_id: previousCampaign.thread_id,
  };

  return (variations ?? [])
    .filter((variation) => {
      const keys = getVariationDependencies(
        variation,
        structureDependencies?.get(variation.key),
      );

      if (
        keys.some(
          (key) =>
            String(previousCampaign[key] ?? "") !== String(next[key] ?? ""),
        )
      ) {
        return true;
      }

      return (
        !!getPromptTemplate &&
        JSON.stringify(
          getPromptTemplate(variation, previousCampaign) ?? null,
        ) !== JSON.stringify(getPromptTemplate(variation, next) ?? null)
      );
    })
    .map((variation) => variation.key);
};

/**
 * Whether an image was uploaded by the user rather than generated: it records
 * none of what a generation does.
 */
export const isUploadedImage = (asset) =>
  !!asset?.filePath &&
  !asset.prompt &&
  !asset.fireflyId &&
  !asset.sourceValue &&
  !asset.sourceReferences;

/** Whether two snapshots recorded the same values. */
export const isSameSnapshot = (a, b) => {
  if (a === b) {
    return true;
  }

  if (!isSnapshot(a) || !isSnapshot(b)) {
    return false;
  }

  const keys = Object.keys(a);

  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => Object.hasOwn(b, key) && a[key] === b[key])
  );
};

const listFormat = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

/** What catches an outdated image up: a new prompt and image, or an image. */
export const OUTDATED_ACTION = {
  PROMPT_AND_IMAGE: "prompt-and-image",
  IMAGE: "image",
};

/**
 * What an outdated image needs — `null` when it is not outdated. A changed
 * campaign value needs a new prompt too, unless the prompt is static and not
 * the user's own (`isPromptEdited`): the pipeline then refreshes it while
 * generating the image. A changed reference alone needs a new image.
 */
export const getOutdatedAction = ({
  changedKeys = [],
  changedReferences = [],
  isStatic = false,
  isPromptEdited = false,
} = {}) => {
  if (changedKeys.length && (!isStatic || isPromptEdited)) {
    return OUTDATED_ACTION.PROMPT_AND_IMAGE;
  }

  return changedKeys.length || changedReferences.length
    ? OUTDATED_ACTION.IMAGE
    : null;
};

/**
 * What tells the user an image is outdated: the changed campaign keys, named
 * by the field showing them (`labels`) or else by the key itself, and the
 * changed reference images (`changedReferences`), named by `referenceLabels`
 * or else by their name — then what to regenerate (`action`, see
 * `getOutdatedAction`).
 */
export const formatOutdatedMessage = (
  changedKeys,
  labels,
  {
    changedReferences = [],
    referenceLabels,
    action = changedKeys?.length
      ? OUTDATED_ACTION.PROMPT_AND_IMAGE
      : OUTDATED_ACTION.IMAGE,
  } = {},
) => {
  const names = Array.from(
    new Set([
      ...(changedKeys ?? []).map((key) => labels?.get(key) ?? key),
      ...changedReferences.map((key) => referenceLabels?.get(key) ?? key),
    ]),
  );

  if (!names.length) {
    return undefined;
  }

  return `${listFormat.format(names.map((name) => `"${name}"`))} changed since this image was generated. Regenerate the ${action === OUTDATED_ACTION.PROMPT_AND_IMAGE ? "prompt and image" : "image"} to match.`;
};
